/**
 * @description Botão flutuante da Celeri para quem está no teste grátis (5.5). Mostra quantos
 *              dias faltam (alerta nos 2 últimos), convida a fazer um pregão de teste com
 *              propostas de fornecedores fictícios, leva à fase interna e, na sala do pregão de
 *              teste, simula uma rodada de lances dos concorrentes. Montado no main.tsx, fora do
 *              layout: não renderiza nada para quem não está em teste.
 *              29/10: botão maior com a Celeri em linhas animadas, "Trocar de perfil" e "Ajuda e
 *              suporte" (WhatsApp do comercial e 0800) ao lado, e tour guiado pelo sistema, que
 *              abre sozinho no primeiro acesso.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/chassis/core/auth/hooks';
import { Lucide } from '@/chassis/ui/components';
import { CeleriLinha } from '@/chassis/ui/components/brand/Celeri';
import { CHANNEL_LABEL, type MyTrial, errorMessage, isoToBr, ymdToBr, trialService } from '../service';
import AjudaSuporte from './AjudaSuporte';
import TourGuiado, { type PassoTour } from './TourGuiado';
import '../trial.css';

const BALAO_KEY = 'cdcb.trial.balaoVisto';
const AUCTIONEER_VIEW = /^\/publictenders\/auctioneer\/view\/([0-9a-f-]{36})/i;
const ROTAS_SEM_BOTAO = ['/login', '/register', '/cadastro', '/recuperar-senha', '/forgot-password'];
const TOUR_KEY = 'cdcb.trial.tour.';

/** Passos do tour (29/10). Os do menu usam o título do item (aria-label); quem não aparece na tela é pulado. */
const PASSOS_TOUR: PassoTour[] = [
    { titulo: 'Bem-vindo ao seu teste', texto: 'Este é o seu ambiente de teste: um órgão só seu, com fornecedores fictícios. Em um minuto eu mostro onde fica cada coisa.' },
    { alvo: 'a.side-menu__link[aria-label="Meu Painel"]', titulo: 'Seu painel', texto: 'O resumo do órgão: licitações, economia, prazos e o que está pendente.' },
    { alvo: 'a.side-menu__link[aria-label="Licitações"], a.side-menu__link[aria-label="Buscar Processos"]', titulo: 'Licitações', texto: 'Aqui você busca, cria, publica e conduz os pregões, da proposta à homologação.' },
    { alvo: 'a.side-menu__link[aria-label="Fase Interna"]', titulo: 'Fase interna com a Celeri', texto: 'Descreva a demanda e a Celeri prepara o DFD, o ETP, o termo de referência e a pesquisa de preços. Depois é só converter em licitação.' },
    { alvo: 'a.side-menu__link[aria-label="Indicadores"]', titulo: 'Indicadores', texto: 'Economia, prazos e produtividade do órgão, por pregoeiro.' },
    { alvo: '[data-testid="topbar-account"]', titulo: 'Trocar de perfil', texto: 'Clique aqui para trocar de perfil quando quiser. O botão "Trocar de perfil" ao lado da Celeri faz o mesmo.' },
    { alvo: '[data-testid="topbar-bell"]', titulo: 'Notificações', texto: 'Dúvidas, impugnações, recursos e mensagens do chat do pregão chegam aqui.' },
    { alvo: '[data-testid="trial-help"]', titulo: 'Ajuda na hora', texto: 'WhatsApp do comercial para tirar dúvida em tempo real e o suporte 0800, 24 horas por dia.' },
    { alvo: '[data-testid="trial-fab"]', titulo: 'Comece pelo pregão de teste', texto: 'Clique na Celeri: eu monto um pregão com fornecedores fictícios para você ver a licitação do começo ao fim.' },
];

function tourFeito(leadId: string): boolean {
    try {
        return localStorage.getItem(TOUR_KEY + leadId) === 'feito';
    } catch {
        return true;
    }
}

function marcarTour(leadId: string) {
    try {
        localStorage.setItem(TOUR_KEY + leadId, 'feito');
    } catch {
        /* sem storage: o tour não volta sozinho nesta página */
    }
}

function balaoJaVisto(endsAt: string): boolean {
    try {
        return sessionStorage.getItem(BALAO_KEY) === endsAt;
    } catch {
        return false;
    }
}

function marcarBalao(endsAt: string) {
    try {
        sessionStorage.setItem(BALAO_KEY, endsAt);
    } catch {
        /* sem storage: o balão volta na próxima página */
    }
}

function diasTexto(trial: MyTrial): string {
    if (trial.expired) return 'Teste encerrado';
    if (trial.daysLeft <= 1) return 'Último dia de teste';
    return `Faltam ${trial.daysLeft} dias`;
}

export default function TrialExperience() {
    const { user, isGuest, status } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [trial, setTrial] = useState<MyTrial | null>(null);
    const [aberto, setAberto] = useState(false);
    const [balao, setBalao] = useState(false);
    const [criando, setCriando] = useState(false);
    const [simulando, setSimulando] = useState(false);
    const [erro, setErro] = useState<string | null>(null);
    const [ok, setOk] = useState<string | null>(null);
    const [ajuda, setAjuda] = useState(false);
    const [tour, setTour] = useState(false);

    const userId = (user as { id?: string } | null)?.id;

    const carregar = useCallback(async () => {
        try {
            const r = await trialService.me();
            setTrial(r?.trial ?? null);
        } catch {
            setTrial(null);
        }
    }, []);

    useEffect(() => {
        if (status !== 'ready' || !userId || isGuest) {
            setTrial(null);
            return;
        }
        void carregar();
    }, [carregar, isGuest, status, userId]);

    useEffect(() => {
        if (trial && !trial.expired && !balaoJaVisto(trial.endsAt)) setBalao(true);
        if (trial && trial.daysLeft <= 2) setBalao(true);
    }, [trial]);

    // Tour automático no primeiro acesso de quem está em teste (uma vez por cadastro).
    const leadId = trial && !trial.expired ? trial.leadId : null;
    useEffect(() => {
        if (!leadId || tourFeito(leadId) || ROTAS_SEM_BOTAO.some((r) => location.pathname.startsWith(r))) return undefined;
        const t = window.setTimeout(() => setTour(true), 1500);
        return () => window.clearTimeout(t);
    }, [leadId, location.pathname]);

    const tenderAtual = useMemo(() => {
        const m = AUCTIONEER_VIEW.exec(location.pathname);
        if (!m || !trial) return null;
        return trial.tenders.find((t) => t.id === m[1]) ?? null;
    }, [location.pathname, trial]);

    if (!trial || ROTAS_SEM_BOTAO.some((r) => location.pathname.startsWith(r))) return null;

    const alerta = trial.expired || trial.daysLeft <= 2;
    const progresso = Math.min(100, Math.round((trial.daysUsed / Math.max(1, trial.totalDays)) * 100));

    const fecharBalao = () => {
        setBalao(false);
        marcarBalao(trial.endsAt);
    };

    const abrirTour = () => {
        setAberto(false);
        setAjuda(false);
        fecharBalao();
        setTour(true);
    };

    const fecharTour = () => {
        setTour(false);
        marcarTour(trial.leadId);
    };

    // A mesma janela de troca de perfil do topo e do menu lateral (evento ouvido pelo layout).
    const trocarPerfil = () => {
        setAberto(false);
        window.dispatchEvent(new CustomEvent('cdcb:open-representation-modal'));
    };

    const abrir = () => {
        fecharBalao();
        setErro(null);
        setOk(null);
        setAberto((v) => !v);
    };

    const criarPregao = async () => {
        setCriando(true);
        setErro(null);
        try {
            const r = await trialService.createTestTender();
            await carregar();
            setAberto(false);
            fecharBalao();
            navigate(r.redirectPath || `/publictenders/auctioneer/view/${r.tenderUuid}`);
        } catch (err) {
            setErro(errorMessage(err, 'Não foi possível criar o pregão de teste agora.'));
        } finally {
            setCriando(false);
        }
    };

    const simular = async () => {
        if (!tenderAtual) return;
        setSimulando(true);
        setErro(null);
        setOk(null);
        try {
            const r = await trialService.simulateBids(tenderAtual.id);
            if (r.bidsCreated > 0) {
                setOk(`${r.bidsCreated} lance(s) dos fornecedores fictícios em ${r.lots} lote(s). Veja na sala de disputa.`);
            } else if (r.rejected?.length) {
                setErro(`Os lances não entraram: ${r.rejected[0].reason}`);
            } else {
                setOk('Nenhum lote está em disputa agora. A rodada de lances funciona enquanto a disputa está aberta.');
            }
        } catch (err) {
            setErro(errorMessage(err, 'Não foi possível simular os lances agora.'));
        } finally {
            setSimulando(false);
        }
    };

    const contato = trial.contact
        ? `Nossa equipe vai falar com você em ${ymdToBr(trial.contact.date)}, às ${trial.contact.time.replace(':', 'h')}, por ${CHANNEL_LABEL[trial.contact.channel]?.toLowerCase() ?? 'telefone'}.`
        : null;

    return (
        <div className="tg-root tg-fab-wrap" data-testid="trial-experience">
            {aberto ? (
                <section className="tg-painel" role="dialog" aria-label="Teste grátis com a Celeri">
                    <header className="tg-painel-cab">
                        <img className="tg-logo" src="/brand/celeri-escuro.svg" alt="Celeri" />
                        <p className="tg-oi">Oi, eu sou a Celeri. Faço os testes com você: monto um pregão com fornecedores fictícios, preparo a fase interna e te mostro o sistema.</p>
                        <button type="button" className="tg-fechar" onClick={() => setAberto(false)} aria-label="Fechar">
                            <Lucide icon="X" className="h-4 w-4" />
                        </button>
                        <div className="tg-dias">
                            {trial.expired ? (
                                <>Seu teste terminou em {isoToBr(trial.endsAt)}</>
                            ) : (
                                <>
                                    <b>{trial.daysLeft <= 1 ? 'Último dia' : `${trial.daysLeft} dias`}</b> de teste grátis, até {isoToBr(trial.endsAt)}
                                </>
                            )}
                        </div>
                        <div className={`tg-barra${alerta ? ' is-alerta' : ''}`} aria-hidden>
                            <i style={{ width: `${progresso}%` }} />
                        </div>
                    </header>

                    {trial.expired ? (
                        <div className="tg-secao">
                            <div className="tg-aviso">O período de teste acabou. Fale com a nossa equipe para prorrogar o acesso ou para levar o Canal ao seu órgão.</div>
                        </div>
                    ) : (
                        <>
                            {alerta ? (
                                <div className="tg-secao">
                                    <div className="tg-aviso">Seu teste está acabando. Aproveite para ver a sessão pública completa e a fase interna com a Celeri.</div>
                                </div>
                            ) : null}

                            <div className="tg-secao tg-secao-tour">
                                <button type="button" className="tg-btn tg-btn-secundario tg-btn-bloco" onClick={abrirTour} data-testid="trial-tour-start">
                                    <Lucide icon="Compass" className="h-4 w-4" /> Fazer o tour guiado pelo sistema
                                </button>
                            </div>

                            {tenderAtual ? (
                                <div className="tg-secao">
                                    <h5><span className="tg-tri" style={{ color: '#7800FF' }} /> Movimente a disputa</h5>
                                    <p>Os concorrentes deste pregão são fictícios. Peça uma rodada de lances e acompanhe na sala de disputa.</p>
                                    <button type="button" className="tg-btn tg-btn-secundario tg-btn-bloco" onClick={simular} disabled={simulando}>
                                        <Lucide icon={simulando ? 'Loader2' : 'Zap'} className={`h-4 w-4 ${simulando ? 'animate-spin' : ''}`} />
                                        {simulando ? 'Lançando...' : 'Simular uma rodada de lances'}
                                    </button>
                                    {ok ? <div className="tg-ok">{ok}</div> : null}
                                    {erro ? <div className="tg-erro" role="alert">{erro}</div> : null}
                                </div>
                            ) : null}

                            <div className="tg-secao">
                                <h5><span className="tg-tri" style={{ color: '#7800FF' }} /> Teste uma licitação do começo ao fim</h5>
                                <p>A Celeri monta um pregão de teste no seu órgão, com propostas de fornecedores fictícios. Você conduz a sessão como pregoeiro(a): propostas, disputa, julgamento, habilitação e homologação.</p>
                                <ul className="tg-passos">
                                    <li><em>3 min</em> propostas</li>
                                    <li><em>10 min</em> disputa</li>
                                    <li>julgamento e homologação</li>
                                </ul>
                                <button type="button" className="tg-btn tg-btn-primario tg-btn-bloco" onClick={criarPregao} disabled={criando} data-testid="trial-create-tender">
                                    <Lucide icon={criando ? 'Loader2' : 'Gavel'} className={`h-4 w-4 ${criando ? 'animate-spin' : ''}`} />
                                    {criando ? 'Montando o pregão...' : 'Fazer um pregão de teste'}
                                </button>
                            </div>

                            <div className="tg-secao">
                                <h5><span className="tg-tri" style={{ color: '#7800FF' }} /> Teste a fase interna</h5>
                                <p>Crie um projeto e peça à Celeri o DFD, o ETP, o termo de referência e a pesquisa de preços. Depois, converta em licitação com um clique.</p>
                                <button
                                    type="button"
                                    className="tg-btn tg-btn-secundario tg-btn-bloco"
                                    onClick={() => {
                                        setAberto(false);
                                        navigate('/internal-phase');
                                    }}
                                >
                                    <Lucide icon="FileText" className="h-4 w-4" /> Abrir a fase interna
                                </button>
                            </div>

                            {erro && !tenderAtual ? <div className="tg-secao"><div className="tg-erro" role="alert">{erro}</div></div> : null}
                        </>
                    )}

                    {trial.tenders.length > 0 ? (
                        <div className="tg-secao">
                            <h5>Seus pregões de teste</h5>
                            <ul className="tg-lista">
                                {trial.tenders.map((t) => (
                                    <li key={t.id}>
                                        <Link to={`/publictenders/auctioneer/view/${t.id}`} onClick={() => setAberto(false)}>
                                            <span>{t.title.replace(/^\[TESTE\]\s*/, '')}</span>
                                            <small>{isoToBr(t.createdAt, true)}</small>
                                        </Link>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ) : null}

                    {contato ? (
                        <div className="tg-secao">
                            <p style={{ margin: 0 }}>{contato}</p>
                        </div>
                    ) : null}
                </section>
            ) : null}

            {!aberto && balao && !tour ? (
                <div className={`tg-balao${alerta ? ' is-alerta' : ''}`} role="status">
                    <button type="button" className="tg-fechar" onClick={fecharBalao} aria-label="Fechar aviso">
                        <Lucide icon="X" className="h-4 w-4" />
                    </button>
                    <h4>{trial.expired ? 'Seu teste terminou' : alerta ? 'Seu teste está acabando' : 'Você está no teste grátis'}</h4>
                    <p>
                        {trial.expired
                            ? 'Fale com a nossa equipe para prorrogar o acesso.'
                            : `${diasTexto(trial)}. Clique aqui para fazer um pregão de teste e entender como funciona o processo da licitação.`}
                    </p>
                    {!trial.expired ? (
                        <button type="button" className="tg-btn tg-btn-primario" onClick={abrir}>
                            <span className="tg-tri" /> Fazer um teste
                        </button>
                    ) : null}
                </div>
            ) : null}

            {/* Ao lado da Celeri: trocar de perfil e ajuda (29/10). */}
            {!aberto ? (
                <div className="tg-acoes">
                    <button type="button" className="tg-acao" onClick={trocarPerfil} data-testid="trial-switch-profile">
                        <Lucide icon="ArrowLeftRight" className="h-4 w-4" /> Trocar de perfil
                    </button>
                    <button type="button" className="tg-acao" onClick={() => { setAberto(false); setAjuda(true); }} data-testid="trial-help">
                        <Lucide icon="Headphones" className="h-4 w-4" /> Ajuda e suporte
                    </button>
                </div>
            ) : null}

            <button
                type="button"
                className={`tg-fab tg-fab--celeri${alerta ? ' is-alerta' : ''}`}
                onClick={abrir}
                aria-expanded={aberto}
                aria-label={`Celeri: teste grátis, ${diasTexto(trial).toLowerCase()}`}
                data-testid="trial-fab"
            >
                <span className="tg-fab-marca" aria-hidden="true">
                    <CeleriLinha animada traco={1.6} className="tg-fab-celeri" />
                </span>
                <span className="tg-fab-txt">
                    <b>Teste grátis</b>
                    <span>{diasTexto(trial)}</span>
                    {!trial.expired ? <em>Clique e eu monto um teste</em> : null}
                </span>
            </button>

            {ajuda ? <AjudaSuporte trial={trial} onFechar={() => setAjuda(false)} onTour={abrirTour} /> : null}
            {tour ? <TourGuiado passos={PASSOS_TOUR} onFechar={fecharTour} /> : null}
        </div>
    );
}
